import React, { useEffect } from 'react';
import { useParams } from 'react-router';
import { useFetch, useUser } from '../../../shared/hooks';
import apiRoutes from '../../../../config/api/routes';
import { getPageMapper } from '../../../../config/api/models';
import { Message, Loading } from '../../../shared/components';
import Page from '../TrajectoryDetail/Page';
import { usePagesByType } from '../../hooks';
import { LogActionTypes, useDataLogging } from '../../../shared/data-logging';
import { useQueryCache } from 'react-query';
import { UrlParams } from '../../../shared/types';
import { setPageWidth } from 'src/modules/shared/slices/privatePageSettings/entity/privatePageSettingsSlice';
import { useAppDispatch } from 'src/modules/shared/hooks/useAppDispatch';

export const TrajectoryStudyGuideBuilder = () => {
  const { isTeacher } = useUser();
  const log = useDataLogging();
  const { bookId, trajectoryId } = useParams<UrlParams>();
  const type = 'study_guide';
  const queryCache = useQueryCache();
  const dispatch = useAppDispatch();

  if (!trajectoryId) throw Error('No trajectoryId found!');
  if (!bookId) throw Error('No bookId found!');

  useEffect(() => {
    dispatch(setPageWidth(100));
    return () => {
      dispatch(setPageWidth());
    };
  }, [dispatch]);

  const {
    success: getPagesSuccess,
    pending: getPagesPending,
    data: getPagesData,
    error: getPagesError,
    refetch: getPagesRefetch,
  } = usePagesByType(type);

  const [addPageResponse, addPage, addPageReset] = useFetch(
    apiRoutes.bookTrajectoryPages(bookId, trajectoryId),
    {
      method: 'POST',
      mapper: getPageMapper,
    }
  );

  useEffect(() => {
    if (!getPagesSuccess) return;
    if (getPagesData && getPagesData.length > 0) return;

    addPage({ body: { type } });
  }, [addPage, getPagesSuccess, getPagesData, type]);

  useEffect(() => {
    if (!addPageResponse.success) return;

    if (isTeacher) {
      log(LogActionTypes.TEACHER_PAGE_CREATE, {
        bookId,
        trajectoryId,
        pageId: addPageResponse?.data?.id,
        type: addPageResponse?.data?.type,
      });
    }

    queryCache.removeQueries('getPages');

    addPageReset();
    getPagesRefetch().then();
  }, [
    addPageResponse,
    getPagesRefetch,
    addPageReset,
    isTeacher,
    queryCache,
    log,
    bookId,
    trajectoryId,
  ]);

  if (getPagesSuccess && getPagesData && getPagesData.length > 1) {
    console.warn(`Trajectory ${trajectoryId} has multiple ${type} pages, it should only have 1.`);
  }

  if (getPagesError) {
    console.warn(`There was a problem loading the page.`);
  }

  return (
    <>
      {(getPagesPending || addPageResponse.pending) && <Loading />}
      {addPageResponse.error && <Message type="error">{addPageResponse.reason}</Message>}
      {getPagesSuccess && getPagesData && getPagesData.length > 0 && (
        <Page type={type} id={getPagesData[0].id} bookId={bookId} />
      )}
    </>
  );
};
